import React, { useState, useCallback, useEffect, Fragment } from "react";
import Form from "@rjsf/core";
import validator from "@rjsf/validator-ajv8";
import Dialog from "../dialog";
import InputWithTooltip from "../InputWithTooltip";
import CloseIcon from "../../icons/closeIcon";
import LoadingButton from "../loadingButton";
import ErrorMessage from "../../components/errorMessage";
import { useAxiosPrivate } from "../../hooks/useAxiosPrivate";
import styles from "./style.module.css";
import formStyles from "../form/style.module.css";

const schema = {
  type: "object",
  required: ["name", "expectedLife"],
  properties: {
    name: {
      title: "Item",
      type: "string",
    },
    expectedLife: {
      title: "Expected Life",
      type: "number",
    },
  },
};

const uiSchema = {
  "ui:classNames": formStyles.formInner,
  "ui:rootFieldId": "add_consumable-modal",
  name: {
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_12}`,
    "ui:placeholder": "Enter Item",
  },
  expectedLife: {
    "ui:widget": InputWithTooltip,
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_12} ${formStyles.tooltip_label}`,
    "ui:placeholder": "Enter Expected Life",
    "ui:options": {
      label: false,
      tooltipText:
        <Fragment>Set the expected life in # of track days (or sessions/miles). This will vary by item, for example, tires might last <strong>12 track days</strong>, and oil changes every <strong>4-5 track days</strong>. If unsure, ask your mechanic. <strong>Tip:</strong> Be conservative at first—round down to encourage earlier checks. Better to check early than late!</Fragment>,
      position: ["right"],
      transform: { top: -15, left: 1 },
      type: "number",
    },
  },
  "ui:submitButtonOptions": {
    props: {
      className: formStyles.form_btn,
    },
    submitText: "Add",
  },
};

const AddConsumableModal = ({
  title = "Add Custom Consumable",
  buttonTitle = "Add",
  consumableData = {},
  open,
  onClose,
}) => {
  const axiosPrivate = useAxiosPrivate();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [id, setID] = useState(null);

  const handleClose = (changePage = false) => {
    setFormData({});
    onClose(changePage);
  };

  const onFormSubmit = (formData) => {
    setFormData(formData);
    if (id) {
      handleUpdateConsumable(formData);
    } else {
      handleAddConsumable(formData);
    }
  };

  const handleAddConsumable = useCallback(
    async (data) => {
      setLoading(true);
      setErrorMessage("");
      try {
        let url = `consumables/add`;
        let options = {
          method: "POST",
          url,
          data,
        };
        await axiosPrivate(options)
          .then((response) => {
            if (response.data.status === 1) {
              handleClose(true);
            } else {
              handleClose();
            }
            setLoading(false);
          })
          .catch((err) => {
            if (err.response) {
              handleClose();
              setLoading(false);
              setErrorMessage(err.response.data.message);
            }
          });
      } catch (error) {
        setFormData({});
        // console.error('error', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleUpdateConsumable = useCallback(
    async (data) => {
      setLoading(true);
      setErrorMessage("");
      try {
        let url = `consumables/${id}`;
        let options = {
          method: "PUT",
          url,
          data,
        };
        await axiosPrivate(options)
          .then((response) => {
            if (response.data.status === 1) {
              handleClose(true);
            } else {
              handleClose();
              // console.error('err.res', response);
            }
            setLoading(false);
          })
          .catch((err) => {
            if (err.response) {
              handleClose();
              setLoading(false);
              setErrorMessage(err.response.data.message);
            }
          });
      } catch (error) {
        // console.error('error', error);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [id]
  );

  useEffect(() => {
    if (consumableData._id) {
      let tempData = { ...consumableData };
      delete tempData._id;
      setID(consumableData._id);
      setFormData(tempData);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumableData]);

  return (
    <Dialog open={open} onClose={() => handleClose(false)} maxWidth="680px">
      <div className={styles.add_consumable_modal}>
        <div className={styles.add_consumable_modal_header}>
          <div className={styles.add_consumable_modal_header_title}>
            {title}
          </div>
          <button
            type="button"
            className={styles.add_consumable_modal_header_close}
            onClick={() => handleClose(false)}
          >
            <CloseIcon />
          </button>
        </div>
        <div className={styles.add_consumable_modal_content}>
          <Form
            className={formStyles.form}
            schema={schema}
            uiSchema={uiSchema}
            formData={formData}
            validator={validator}
            showErrorList={false}
            omitExtraData={true}
            liveValidate={false}
            onSubmit={({ formData }) => {
              onFormSubmit(formData);
            }}
          >
            <div className={formStyles.form_actions}>
              <LoadingButton
                type="submit"
                loading={loading}
                title={buttonTitle}
              />
            </div>
          </Form>
          {errorMessage && <ErrorMessage message={errorMessage} mt />}
        </div>
      </div>
    </Dialog>
  );
};

export default AddConsumableModal;
